// src/pages/MenuPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  Typography,
  Paper,
  Container,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
// import CalculateIcon from '@mui/icons-material/Calculate';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import Header from '../components/Header';
import { DrawerCard } from '../components/DrawerCard';
import type { CashDrawer } from '../api/types';
import { getCashDrawer } from 'api';
import { getNewStoreData } from 'services/util';

const MenuPage: React.FC = () => {
  const [drawer, setDrawer] = useState<CashDrawer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const { storeLabel, storeId } = getNewStoreData();

  const navigate = useNavigate();
  const theme = useTheme();
  const isLandscape = useMediaQuery('(orientation: landscape)');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getCashDrawer();
        if (mounted) setDrawer(data ?? null);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('Failed to fetch cash drawer', err);
        if (mounted) {
          setError(`Unable to load cash drawer status: ${msg}`);
          setToastOpen(true);
        }

      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const isActive = drawer?.status === 'active';

  const statusChip = useMemo(() => {
    const label = drawer?.status
      ? drawer.status.charAt(0).toUpperCase() + drawer.status.slice(1)
      : 'Unknown';
    const color: 'default' | 'success' | 'warning' =
      drawer?.status === 'active' ? 'success' : drawer?.status === 'inactive' ? 'warning' : 'default';

    return (
      <Chip
        size="small"
        label={label}
        color={color}
        variant={color === 'default' ? 'outlined' : 'filled'}
        sx={{ borderRadius: 2 }}
      />
    );
  }, [drawer?.status]);

  const disabledHint = isActive ? undefined : 'Open the drawer to enable this action';

  const cards = useMemo(
    () => [
      {
        label: 'Paid In/Out',
        icon: <SyncAltIcon />,
        enabled: !!isActive,
        onClick: () => navigate('/paid-in-out'),
        subtitle: 'Record cash movements',
      },
      // {
      //   label: 'Mid-day Count',
      //   icon: <CalculateIcon />,
      //   enabled: !!isActive,
      //   onClick: () => navigate('/mid-day-count', { state: { drawer } }),
      //   subtitle: 'Perform mid day operation',
      // },
      {
        label: 'Start of Day',
        icon: <LockOpenIcon />,
        enabled: !isActive,
        onClick: () => navigate('/start-of-day', { state: { drawer } }),
        subtitle: 'Open the cash drawer',
      },
      {
        label: 'End of Day',
        icon: <LockIcon />,
        enabled: !!isActive,
        onClick: () => navigate('/end-of-day', { state: { drawer } }),
        subtitle: 'Close and reconcile',
      },
      {
        label: 'Summary',
        icon: <ReceiptLongIcon />,
        enabled: !!isActive,
        onClick: () => navigate('/summary'),
        subtitle: 'Review today’s activity',
      },
    ],
    [drawer, isActive, navigate]
  );

  const handleToastClose = (_e?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') return;
    setToastOpen(false);
  };



  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Cash Management</Typography>}
        subtitle="Choose an action"
        align="left"
        sticky
        elevateOnScroll
        rightSlot={statusChip}
      />



      {/* Tiles panel */}
      <Container
        disableGutters
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 720, md: 880 },
          mx: 'auto',
        }}
      >
        {/* Drawer context / status card */}
        <Card
          sx={{
            mx: 2,
            mt: 2,
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          }}
        >
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Location: <strong>{storeLabel ?? '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drawer ID: <strong>{storeId ?? '—'}</strong>
            </Typography>
          </CardContent>
        </Card>
        <Paper
          elevation={0}
          component="main"
          sx={{
            mx: 2,
            mt: 2,
            mb: 4,
            p: 2,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            background: alpha(theme.palette.primary.light, 0.05),
            backdropFilter: 'saturate(120%) blur(4px)',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: 'repeat(auto-fit, minmax(150px, 1fr))',
                sm: 'repeat(auto-fit, minmax(170px, 1fr))',
                md: isLandscape
                  ? 'repeat(auto-fit, minmax(220px, 1fr))'
                  : 'repeat(auto-fit, minmax(180px, 1fr))',
              },
              maxWidth: { xs: '100%', sm: 720, md: isLandscape ? 980 : 880 },
              mx: 'auto',
            }}
          >
            {cards.map((card) => (
              <DrawerCard
                key={card.label}
                label={card.label}
                icon={card.icon}
                enabled={card.enabled}
                onClick={card.onClick}
                subtitle={card.subtitle}
                variant='outlined'
                disabledTooltip={disabledHint}
                sx={{
                  // extra polish for this page
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                }}
              />
            ))}
          </Box>

          {/* Inline error (in addition to toast) */}
          {!loading && error && (
            <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Container>
      {/* Loading overlay */}
      <Backdrop sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      // onClose={handleToastClose}
      >
        <Alert onClose={handleToastClose} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MenuPage;
